import React, { useState, useCallback } from 'react';
import Link from 'next/link';  
import { buildClassName } from '../../utils';
import { ChevronIcon } from '../desktop/internal/ChevronIcon';
import type { NavigationButton, MenuItem } from '../../types';
import type { NavigationActionHandler } from '../../types/base';
import type { 
  MobileNavigationProps, 
  MobileDividerProps, 
  MobileExpandableItemProps, 
  MobileNavigationItemProps, 
} from './types';
import styles from './MobileNavigation.module.scss';

/**
 * Utility function to handle navigation item clicks and actions
 */
const useNavigationHandler = (
  item: NavigationButton | MenuItem | undefined,
  onItemClick?: () => void,
  onAction?: NavigationActionHandler
) => {
  return useCallback(() => {
    // Handle special actions first
    if ('type' in item && item.type === 'action' && item.metadata?.handler && onAction) {
      onAction(item.metadata.handler, item.metadata.params);
      return;
    }
    
    if (onItemClick) {
      onItemClick();
    }
  }, [item, onItemClick, onAction]);
};

const MobileNavigationDivider = ({ variant = 'primary' }: MobileDividerProps) => (
  <div className={buildClassName(styles.divider, styles[`divider-${variant}`])} />
);

const MobileExpandableItem = ({ 
  label, 
  items, 
  isExpanded, 
  onToggle, 
  onItemClick, 
  onAction,
  variant = 'primary',
}: MobileExpandableItemProps) => {
  return (
    <li className={styles.expandableItem}>
      <div
        className={buildClassName(styles.itemHeader, isExpanded && styles.active)}
        onClick={onToggle}
      >
        <span className={buildClassName(styles.itemLabel, styles[`itemLabel-${variant}`])}>{label}</span>
        <ChevronIcon 
          isExpanded={isExpanded}
          className={styles.chevron}
          width={24}
          height={24}
        />
      </div>
      {isExpanded && items && (
        <ul className={styles.nestedMenu}>
          {items.map((nestedItem, nestedIndex) => {
            if (nestedItem.type === 'divider') {
              return <MobileNavigationDivider key={`nested-divider-${nestedIndex}`} variant="secondary" />;
            }
            
            if (nestedItem.type === 'link' || nestedItem.type === 'action') {
              return (
                <MobileNavigationItem
                  key={nestedItem.id}
                  item={nestedItem}
                  onItemClick={onItemClick}
                  onAction={onAction}
                  isNested
                  parentVariant={variant}
                />
              );
            }
            
            return null;
          })}
        </ul>
      )}
    </li>
  );
};

const MobileNavigationItem = ({ item, onItemClick, onAction, isNested = false, parentVariant }: MobileNavigationItemProps) => {
  const handleClick = useNavigationHandler(item, onItemClick, onAction);
  
  const linkClassName = isNested 
    ? buildClassName(styles.nestedLink, parentVariant === 'secondary' && styles.nestedLinkSecondary)
    : styles.menuLink;
  const itemClassName = isNested ? styles.nestedItem : styles.menuItem;

  // Handle action items
  if ('type' in item && item.type === 'action') {
    return (
      <li className={itemClassName}>
        <button
          className={linkClassName}
          onClick={handleClick}
          type="button"
        >
          {item.label}
        </button>
      </li>
    );
  }
  
  // Handle items with href
  if ('href' in item && item.href) {
    if (item.external) {
      return (
        <li className={itemClassName}>
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
            onClick={handleClick}
          >
            {item.label}
          </a>
        </li>
      );
    }

    return (
      <li className={itemClassName}>
        <Link
          href={item.href}
          className={linkClassName}
          onClick={handleClick}
        >
          {item.label}
        </Link>
      </li>
    );
  }

  return null;
};

export const MobileNavigation = ({ 
  items, 
  onItemClick, 
  onAction,
  expanded = false,
}: MobileNavigationProps) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const isItemExpanded = useCallback((itemId: string): boolean => {
    return expanded || Boolean(expandedItems[itemId]);
  }, [expanded, expandedItems]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const renderNavigationItem = useCallback((item: NavigationButton) => {
    switch (item.buttonVariant) {
      case 'divider':
        return <MobileNavigationDivider />;

      case 'dropdown':
        return (
          <MobileExpandableItem
            label={item.label || ''}
            items={item.items}
            isExpanded={isItemExpanded(item.id)}
            onToggle={() => toggleExpand(item.id)}
            onItemClick={onItemClick}
            onAction={onAction}
            variant="primary"
          />
        );

      case 'link':
        return (
          <MobileNavigationItem
            item={item}
            onItemClick={onItemClick}
            onAction={onAction}
          />
        );

      case 'custom':
        return item.customContent;

      default:
        return null;
    }
  }, [isItemExpanded, toggleExpand, onItemClick, onAction]);

  return (
    <nav className={styles.mobileNav}>
      <ul className={styles.menuItems}>
        {items.map((item, index) => {
          const key = item.buttonVariant === 'divider' ? `divider-${index}` : item.id;
          return <React.Fragment key={key}>{renderNavigationItem(item)}</React.Fragment>;
        })}
      </ul>
    </nav>
  );
};

// Default export for easier imports
export default MobileNavigation; 